import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined, StopOutlined } from '@ant-design/icons';
import { NodeAccessProfileStatus } from '@prisma/client';
import { Button, Descriptions, ModalProps, Result, Space, Spin } from 'antd';
import Dropdown, { DropdownButtonProps } from 'antd/es/dropdown';

import { ApprovalStage, ProfileWithDetails, ProfileWithNextLevelUser } from '@app/domain-t3-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Common } from '@app/web-pages';

import { ProfileOperationRenderer, ProfileStatusRenderer } from '../atoms';
import { useCancelProfile, useGetProfile, useRemoveProfile } from '../data';

export const useProfileActions = () => {
  const [detailsId, setDetailsId] = useState<number | undefined>();
  const [deleteId, setDeleteId] = useState<number | undefined>();

  const navigate = useNavigate();

  const detailsProfile = useGetProfile(detailsId ?? 0, { enabled: !!detailsId });
  const deleteProfile = useGetProfile(deleteId ?? 0, { enabled: !!deleteId });

  const removeMutation = useRemoveProfile();
  const cancelMutation = useCancelProfile();

  const toggleDetailsModal = (id?: number) => {
    setDeleteId(undefined);
    setDetailsId(id);
  };

  const toggleDeleteModal = (id?: number) => {
    setDetailsId(undefined);
    setDeleteId(id);
  };

  const _getDescriptionItems = (data?: ProfileWithNextLevelUser) => {
    if (!data) return [];
    const items = [
      {
        key: 'Name',
        children: (
          <Common.UserPopover user={data.user}>
            <Button type="link" size="small" style={{ padding: 0 }}>
              {data.user?.name}
            </Button>
          </Common.UserPopover>
        ),
        label: 'Name',
      },
      {
        key: 'CW Sponsor',
        children: (
          <Common.UserPopover user={data.nextLevelUser}>
            <Button type="link" size="small" style={{ padding: 0 }}>
              {data.nextLevelUser?.name}
            </Button>
          </Common.UserPopover>
        ),
        label: 'CW Sponsor',
      },
      { key: 'CW Job Title', children: data.user?.jobRole, label: 'CW Job Title' },
      { key: 'CW Work Location', children: data.user?.workLocationBuildingCode, label: 'CW Work Location' },
      { key: 'Project', children: data.die?.name, label: 'Project' },
      { key: 'Node', children: data.node?.name, label: 'Node' },
      { key: 'Operation', children: <ProfileOperationRenderer operation={data.operation} />, label: 'Operation' },
      { key: 'Status', children: <ProfileStatusRenderer status={data.status} />, label: 'Status' },
      { key: 'Approval Stage', children: data.approvalStep?.stage.name, label: 'Approval Stage' },
      { key: 'Contract Name', children: data.supplierContract?.contractName, label: 'Contract Name' },
      { key: 'Contract Number', children: data.supplierContract?.contractNumber, label: 'Contract Number' },
      {
        key: 'Intel Signed Date',
        children: formatIsoDate(data?.supplierContract?.intelSignedDate),
        label: 'Intel Signed Date',
      },
      { key: 'Created By', children: data?.createdByUser.name, label: 'Created By' },
      { key: 'Updated By', children: data?.updatedByUser.name, label: 'Updated By' },
      { key: 'Justification', children: data?.justification, label: 'Justification' },
    ];

    return items;
  };

  const getActionButton = (record: ProfileWithDetails, props?: DropdownButtonProps) =>
    record.status === NodeAccessProfileStatus.PENDING &&
    record.approvalStep?.stage?.name === ApprovalStage.EXTERNAL_TSMC ? (
      <Button size="small" icon={<EyeOutlined />} onClick={() => toggleDetailsModal(record.id)}>
        Details
      </Button>
    ) : (
      <Dropdown.Button
        size="small"
        {...props}
        menu={{
          items:
            record.status === NodeAccessProfileStatus.APPROVED
              ? [
                  { label: 'Edit', key: 'edit', icon: <EditOutlined /> },
                  { label: 'Delete', key: 'delete', icon: <DeleteOutlined />, danger: true },
                ]
              : [{ label: 'Cancel', key: 'delete', icon: <StopOutlined />, danger: true }],
          onClick: (e) => {
            switch (e.key) {
              case 'delete':
                toggleDeleteModal(record.id);
                break;
              case 'edit':
                navigate(`./edit/${record.id}`);
                break;
              default:
                console.log(`Cannot handle key: ${e.key}.`);
                break;
            }
          },
        }}
        onClick={() => toggleDetailsModal(record.id)}
      >
        <Space>
          <EyeOutlined />
          Details
        </Space>
      </Dropdown.Button>
    );

  const getDetailsModalProps = (props?: ModalProps): ModalProps => {
    const footer = [
      ...(detailsProfile.data?.status === NodeAccessProfileStatus.APPROVED
        ? [
            <Button key="delete" onClick={() => toggleDeleteModal(detailsId)} icon={<DeleteOutlined />} danger>
              Delete
            </Button>,
          ]
        : detailsProfile.data?.status === NodeAccessProfileStatus.PENDING &&
            detailsProfile.data?.approvalStep?.stage?.name === ApprovalStage.EXTERNAL_TSMC
          ? []
          : [
              <Button key="cancel" onClick={() => toggleDeleteModal(detailsId)} icon={<StopOutlined />} danger>
                Cancel
              </Button>,
            ]),
      <Button key="close" onClick={() => toggleDetailsModal()} icon={<CloseCircleOutlined />} type="primary">
        Close
      </Button>,
    ];

    return {
      centered: true,
      title: `${detailsProfile.data?.user.name} Details`,
      width: 1100,
      ...props,

      children: detailsProfile.isFetching ? (
        <Spin style={{ width: '100%' }} />
      ) : (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Descriptions
            bordered
            size="small"
            layout="horizontal"
            labelStyle={{ width: 150 }}
            column={1}
            items={_getDescriptionItems(detailsProfile.data)}
          />
        </Space>
      ),
      footer,
      open: !!detailsId,
      onCancel: () => toggleDetailsModal(),
    };
  };

  const getDeleteModalProps = (props?: ModalProps): ModalProps => {
    const isRemove = deleteProfile.data?.status === NodeAccessProfileStatus.APPROVED;
    const mutator = isRemove ? removeMutation : cancelMutation;
    const onCancel = () => {
      toggleDeleteModal();
      mutator.reset();
    };

    const onOk = () => {
      if (mutator.isIdle || mutator.isError) {
        deleteId && mutator.mutate(deleteId);
      } else {
        onCancel();
      }
    };

    return {
      centered: true,
      title: `Are you sure you want to ${isRemove ? 'delete' : 'cancel'} this profile?`,
      ...props,
      children: mutator.isPending ? (
        <Spin style={{ width: '100%' }} />
      ) : mutator.isSuccess ? (
        <Result
          status="success"
          title={`Successfully ${isRemove ? 'deleted' : 'canceled'} profile`}
          subTitle={`Profile for ${deleteProfile.data?.user.name} on Contract ${deleteProfile.data?.supplierContract.contractName} has been ${isRemove ? 'deleted' : 'canceled'}`}
        />
      ) : mutator.isError ? (
        <Result
          status="error"
          title={`Error occured when ${isRemove ? 'deleted' : 'canceled'} profile`}
          subTitle={mutator.error?.response?.data?.message}
        />
      ) : (
        <Descriptions
          bordered
          size="small"
          layout="horizontal"
          column={1}
          labelStyle={{ width: 150 }}
          items={_getDescriptionItems(deleteProfile.data)}
        />
      ),
      cancelText: 'No',
      cancelButtonProps: {
        style: mutator.isSuccess ? { display: 'none' } : undefined,
        disabled: mutator.isPending,
      },
      okButtonProps: {
        icon: mutator.isSuccess ? <CloseCircleOutlined /> : isRemove ? <DeleteOutlined /> : <StopOutlined />,
        disabled: mutator.isPending,
      },
      okText:
        mutator.isIdle || mutator.isPending ? (isRemove ? 'Delete' : 'Cancel') : mutator.isError ? 'Retry' : 'Close',
      okType: mutator.isSuccess ? undefined : 'danger',
      open: !!deleteId,
      width: 800,
      // If enabled field for a query is set. Query invalidation is ignored by react-query. Hence using this approach.
      afterOpenChange: (open) => open && deleteProfile.refetch(),
      destroyOnClose: true,
      onCancel,
      onOk,
    };
  };

  return { toggleDetailsModal, toggleDeleteModal, getActionButton, getDetailsModalProps, getDeleteModalProps };
};
